import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import NSJobComponent from '../../../job.js';

import Company from '../../../../../models/company.js';
import JobSchema from '../../../../../models/job-schema.js';

import { callLambda } from '../../../../../helpers/lambda.js';
import {
  companiesHouseFilingUrl,
  formatTurnoverAmount,
  formatTurnoverDate,
} from '../../../../../helpers/turnover-format.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/turnover/dispute-review.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSCompanyTurnoverDisputeReview.is)
export default class NSCompanyTurnoverDisputeReview extends NSJobComponent {
  static get is() {
    return 'ns-company-turnover-dispute-review';
  }

  @state()
  private __company?: Company;

  @state()
  private __companyName = 'this company';

  @state()
  private __formattedAmount = 'N/A';

  @state()
  private __formattedYearEnding = 'N/A';

  @state()
  private __resolutionMethod = 'N/A';

  @state()
  private __transactionId = 'N/A';

  @state()
  private __filingHistoryUrl: string | null = null;

  @state()
  private __selectedOutcome = 'confirmed';

  @state()
  private __submitLabel = 'Confirm';

  @state()
  private __correctedAmount = '';

  @state()
  private __correctedYear = '';

  @state()
  private __correctedTransactionId = '';

  @state()
  private __correctedFiledOn = '';

  @state()
  private __reviewNotes = '';

  @state()
  private __errors: {
    selection?: string;
    reviewNotes?: string;
    correctedAmount?: string;
    correctedYear?: string;
    correctedTransactionId?: string;
    correctedFiledOn?: string;
  } = {};

  private __outcomeOptions = [
    { value: 'confirmed', label: 'Confirmed Correct' },
    { value: 'corrected', label: 'Submit Corrected Figure' },
    { value: 'unknown', label: "Can't Determine — Ask Customer" },
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { confirmed: { type: 'hidden' }, corrected: { type: 'hidden' }, unknown: { type: 'hidden' } },
    });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .primary-info ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .primary-info ns-detail-list::part(value) {
        font-size: var(--wa-font-size-m);
      }

      .error-text {
        font-size: var(--wa-font-size-m);
      }

      textarea {
        width: 100%;
        min-height: 60px;
        box-sizing: border-box;
        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border: none;
        border-radius: var(--wa-border-radius-s);
        background: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
        font: inherit;
        resize: vertical;
      }

      textarea::placeholder {
        color: var(--wa-color-text-quiet);
      }

      textarea:focus-within {
        outline: 2px solid var(--primary-accent-color);
        outline-offset: -2px;
        box-shadow: none;
      }
    `,
  ];

  async connectedCallback() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      // This job is assigned to nodeStream/SUPPORT for queue purposes (see companyId on this
      // job's own workflow node), so it does NOT identify the disputed company — that comes
      // through separately as disputedCompanyId.
      this.__company = await this.loadInput('company', 'disputedCompanyId');

      this.__populateCompanyDisplayValues();
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  private __populateCompanyDisplayValues() {
    if (!this.__company) return;

    this.__companyName = this.__company.name ?? 'this company';
    this.__formattedAmount = formatTurnoverAmount(this.__company.turnover);
    this.__formattedYearEnding = formatTurnoverDate(this.__company.turnover?.year);
    this.__resolutionMethod = this.__company.turnover?.resolutionMethod ?? 'N/A';
    this.__transactionId = this.__company.turnover?.transactionId ?? 'N/A';
    this.__filingHistoryUrl = companiesHouseFilingUrl(this.__company, this.__company.turnover?.transactionId);
  }

  /**
   * Parses a strict DD-MM-YYYY string (matching this form's placeholder) into an ISO
   * YYYY-MM-DD string, rejecting anything that isn't a real calendar date — new Date(string)
   * would otherwise silently roll an invalid date like "31-02-2024" over into March rather
   * than reject it.
   */
  private __parseUKDate(value: string): string | null {
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);

    const date = new Date(Date.UTC(year, month - 1, day));
    const isRealDate = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    if (!isRealDate) return null;

    return date.toISOString().slice(0, 10);
  }

  private __validateReviewNotes(): boolean {
    if (!this.__reviewNotes.trim()) {
      this.__errors = { ...this.__errors, reviewNotes: 'Please record what you found before submitting' };
      return false;
    }
    return true;
  }

  private __selectOutcome(value: string) {
    this.__selectedOutcome = value;
    this.__errors = {};

    if (value === 'confirmed') this.__submitLabel = 'Confirm';
    else if (value === 'corrected') this.__submitLabel = 'Submit Corrected Figure';
    else if (value === 'unknown') this.__submitLabel = 'Ask Customer to Declare';
    else this.__submitLabel = 'Submit';
  }

  private async __handleSubmit() {
    if (this.__selectedOutcome === 'confirmed') {
      await this._outcomeConfirmedHandler();
      return;
    }
    if (this.__selectedOutcome === 'corrected') {
      await this._outcomeCorrectedHandler();
      return;
    }
    if (this.__selectedOutcome === 'unknown') {
      await this._outcomeUnknownHandler();
    }
  }

  async _outcomeConfirmedHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();
    this.__errors = {};

    if (!this.__validateReviewNotes()) return;

    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__company) throw new Error('Missing company');

      this.loading('Recording review');

      await callLambda(
        'company/turnover-dispute-review-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          outcome: 'confirmed',
          notes: this.__reviewNotes.trim(),
        }),
      );

      await this.outcome('confirmed', { reviewNotes: this.__reviewNotes.trim() });
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  async _outcomeCorrectedHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();
    this.__errors = {};

    if (!this.__validateReviewNotes()) return;

    const trimmedAmount = this.__correctedAmount.trim();
    const amount = Number(trimmedAmount);
    if (!trimmedAmount || Number.isNaN(amount)) {
      this.__errors = { ...this.__errors, correctedAmount: 'Please enter the correct turnover as a number' };
      return;
    }

    const correctedYear = this.__parseUKDate(this.__correctedYear.trim());
    if (!correctedYear) {
      this.__errors = { ...this.__errors, correctedYear: 'Please enter a valid date in DD-MM-YYYY format' };
      return;
    }

    const correctedTransactionId = this.__correctedTransactionId.trim();
    if (!correctedTransactionId) {
      this.__errors = {
        ...this.__errors,
        correctedTransactionId: 'Please enter the filing transaction ID you sourced this figure from',
      };
      return;
    }

    const correctedFiledOn = this.__parseUKDate(this.__correctedFiledOn.trim());
    if (!correctedFiledOn) {
      this.__errors = { ...this.__errors, correctedFiledOn: 'Please enter a valid date in DD-MM-YYYY format' };
      return;
    }

    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__company) throw new Error('Missing company');

      this.loading('Submitting corrected turnover');

      await callLambda(
        'company/turnover-dispute-review-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          outcome: 'corrected',
          notes: this.__reviewNotes.trim(),
          correctedAmount: amount,
          correctedYear,
          correctedTransactionId,
          correctedFiledOn,
        }),
      );

      await this.outcome('corrected', {
        correctedAmount: String(amount),
        correctedYear,
        correctedTransactionId,
        correctedFiledOn,
        reviewNotes: this.__reviewNotes.trim(),
      });
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  async _outcomeUnknownHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();
    this.__errors = {};

    if (!this.__validateReviewNotes()) return;

    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__company) throw new Error('Missing company');

      this.loading('Recording review');

      await callLambda(
        'company/turnover-dispute-review-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          outcome: 'unknown',
          notes: this.__reviewNotes.trim(),
        }),
      );

      await this.outcome('unknown', { reviewNotes: this.__reviewNotes.trim() });
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  private __renderOutcomePicker() {
    return html`
      <div class="job-panel">
        <wa-radio-group
          label="What's the outcome of your review?"
          .value=${this.__selectedOutcome}
          @change=${(e: Event) => this.__selectOutcome((e.target as WaRadioGroup).value as string)}
        >
          ${this.__outcomeOptions.map(
            (option) => html`
              <wa-radio value=${option.value}>${option.label}</wa-radio>
            `,
          )}
        </wa-radio-group>
        ${this.__errors.selection
          ? html`
              <div class="error-text">${this.__errors.selection}</div>
            `
          : undefined}
      </div>
    `;
  }

  private __renderPrimaryInfo() {
    return html`
      <div slot="aside" class="primary-info">
        <span class="info-icon" aria-hidden="true">i</span>
        <p>
          Check the filing linked below. If it's correct, confirm it — the customer will be shown a notice explaining
          they'll need to update their own Companies House record if they still disagree. If it's wrong, either submit
          the correct figure (the customer will be asked to confirm it) or, if you can't determine one, ask the customer
          to declare it themselves.
        </p>
        <ns-detail-list
          .rows=${[
            { label: 'Company', value: this.__companyName },
            { label: 'Turnover shown to the user', value: this.__formattedAmount },
            { label: 'For the year ending', value: this.__formattedYearEnding },
            { label: 'Extracted via', value: this.__resolutionMethod },
            { label: 'Filing transaction ID', value: this.__transactionId },
            ...(this.__filingHistoryUrl
              ? [
                  {
                    label: 'Source filing',
                    value: html`
                      <a href="${this.__filingHistoryUrl}" target="_blank" rel="noopener noreferrer">
                        ${this.__company?.turnover?.transactionId
                          ? 'View this filing on Companies House'
                          : "View this company's filing history on Companies House"}
                      </a>
                    `,
                  },
                ]
              : []),
          ]}
        ></ns-detail-list>
      </div>
    `;
  }

  private __renderForm() {
    return html`
      <div class="job-panel">
        ${this.__selectedOutcome === 'corrected'
          ? html`
              <div>
                <ns-input-field
                  stacked
                  label="Corrected turnover"
                  type="number"
                  placeholder="The correct turnover figure"
                  .value=${this.__correctedAmount}
                  @input=${(e: Event) => {
                    const target = e.target as unknown as { value: string };
                    this.__correctedAmount = target.value;
                  }}
                ></ns-input-field>
                ${this.__errors.correctedAmount
                  ? html`
                      <div class="error-text">${this.__errors.correctedAmount}</div>
                    `
                  : undefined}
              </div>

              <div>
                <ns-input-field
                  stacked
                  label="Corrected period end"
                  placeholder="e.g. 31-12-2024"
                  .value=${this.__correctedYear}
                  @input=${(e: Event) => {
                    const target = e.target as unknown as { value: string };
                    this.__correctedYear = target.value;
                  }}
                ></ns-input-field>
                ${this.__errors.correctedYear
                  ? html`
                      <div class="error-text">${this.__errors.correctedYear}</div>
                    `
                  : undefined}
              </div>

              <div>
                <ns-input-field
                  stacked
                  label="Filing transaction ID"
                  placeholder="Copy from the filing's URL on Companies House"
                  .value=${this.__correctedTransactionId}
                  @input=${(e: Event) => {
                    const target = e.target as unknown as { value: string };
                    this.__correctedTransactionId = target.value;
                  }}
                ></ns-input-field>
                ${this.__errors.correctedTransactionId
                  ? html`
                      <div class="error-text">${this.__errors.correctedTransactionId}</div>
                    `
                  : undefined}
              </div>

              <div>
                <ns-input-field
                  stacked
                  label="Filed with Companies House on"
                  placeholder="e.g. 15-03-2025"
                  .value=${this.__correctedFiledOn}
                  @input=${(e: Event) => {
                    const target = e.target as unknown as { value: string };
                    this.__correctedFiledOn = target.value;
                  }}
                ></ns-input-field>
                ${this.__errors.correctedFiledOn
                  ? html`
                      <div class="error-text">${this.__errors.correctedFiledOn}</div>
                    `
                  : undefined}
              </div>
            `
          : nothing}

        <div class="layout vertical">
          <label class="small mb-1" for="review-notes">Review notes</label>
          <textarea
            id="review-notes"
            .value=${this.__reviewNotes}
            placeholder="What did you find in the filing?"
            @input=${(e: InputEvent) => (this.__reviewNotes = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
          ${this.__errors.reviewNotes
            ? html`
                <div class="error-text">${this.__errors.reviewNotes}</div>
              `
            : undefined}
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="visually-hidden" aria-live="polite" aria-atomic="true">
        ${Object.values(this.__errors).filter(Boolean).join(' ')}
      </div>
      <ns-job-layout>
        ${this.__renderOutcomePicker()} ${this.__renderForm()} ${this.__renderPrimaryInfo()}
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button
        variant="brand"
        type="button"
        ?disabled=${!this.__selectedOutcome}
        @click=${() => this.__handleSubmit()}
      >
        ${this.__submitLabel}
      </wa-button>
    `;
  }
}
